import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Container, Row, Col, Spinner, Accordion } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  FaCheckCircle,
  FaBuilding,
  FaUserTie,
  FaDownload,
  FaArrowRight,
  FaCut,
  FaDog,
  FaCar,
  FaHeartbeat,
  FaFootballBall,
  FaWallet,
  FaAddressBook,
  FaPalette,
  FaQuestionCircle,
  FaBolt
} from '../components/icons/faLite'; // TASK-157: fuera de react-icons/fa para no inflar main.js
import { useAuth } from '../context/authContext';
import usePWAInstall from '../hooks/usePWAInstall';
import { toast } from 'sonner';
import '../styles/home.css';
import SEOMeta from '../components/SEOMeta';
import { PAGE_SEO, SITE_URL } from '../seo/seoPages'; // TASK-153: misma fuente que el prerender
import PublicLayout from '../components/PublicLayout';
import { PLANES, TRIAL_DAYS, formatPrecioARS } from '../config/pricingConfig'; // TASK-017: precios unificados con /precios y MP

// Vercel React Best Practices: bundle-dynamic-imports (Lazy Load Heavy Forms specifically ReCaptcha)
const LoginForm = lazy(() => import('../components/forms/LoginForm'));
const RegisterForm = lazy(() => import('../components/forms/RegisterForm'));
// TASK-157: el configurador de planes solo se abre a pedido; no va en el bundle inicial
const PlanBuilder = lazy(() => import('../components/pricing/PlanBuilder'));

const HomePage = () => {

  const [isLoginView, setIsLoginView] = useState(true);
  const [activeTab, setActiveTab] = useState('peluqueria');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [isPlanBuilderOpen, setIsPlanBuilderOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const { canInstall, isIOS, isAndroid, promptInstall } = usePWAInstall();

  const handleInstallClick = () => {
    if (isIOS) {
      toast.info(
        '📲 Para instalar en iPhone: pulsa el botón "Compartir" (abajo en Safari) y luego "Añadir a la pantalla de inicio".',
        { position: "bottom-center", autoClose: 8000 }
      );
    } else if (isAndroid && !canInstall) {
      toast.info(
        '📲 Para instalar en Android: pulsa los tres puntos (⋮) en Chrome y elige "Instalar aplicación".',
        { position: "bottom-center", autoClose: 8000 }
      );
    } else {
      promptInstall();
    }
  };

  // Efecto para centrar el scroll en la tarjeta de Login cuando se presiona "Ingresar" en el Navbar
  useEffect(() => {
    const scrollToLogin = () => {
      setIsLoginView(true);
      setTimeout(() => {
        const card = document.getElementById('login-card');
        if (card) {
          card.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const emailInput = document.getElementById('loginEmail');
          if (emailInput) emailInput.focus();
        }
      }, 150);
    };

    const scrollToRegister = () => {
      setIsLoginView(false);
      setTimeout(() => {
        const card = document.getElementById('login-card');
        if (card) {
          card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 150);
    };

    window.addEventListener('open-login-view', scrollToLogin);
    window.addEventListener('open-register-view', scrollToRegister);

    // Revisar si la URL incluye ?login=true o #login
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    if (params.get('login') === 'true' || hash === '#login' || hash === '#login-card') {
      scrollToLogin();
    } else if (params.get('register') === 'true' || hash === '#register') {
      scrollToRegister();
    }

    return () => {
      window.removeEventListener('open-login-view', scrollToLogin);
      window.removeEventListener('open-register-view', scrollToRegister);
    };
  }, []);

  const planPrincipal = PLANES.find((p) => p.destacado) || PLANES.find((p) => p.id === 'profesional');

  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "uTurno",
    "operatingSystem": "Web",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": String(planPrincipal.precioMensual),
      "priceCurrency": "ARS"
    }
  };

  return (
    <PublicLayout showFooter={true}>
      <SEOMeta
        title={PAGE_SEO.home.title}
        description={PAGE_SEO.home.description}
        canonicalUrl={`${SITE_URL}${PAGE_SEO.home.path}`}
        schema={homeSchema}
      />

      <main>
        {/* =======================================
            SECCIÓN 1: HERO SECTION
        ======================================= */}
        <section className="hero-section py-4 py-md-5 position-relative">
          <Container className="hero-content-wrapper px-3">
            <Row className="align-items-center gy-4 gy-md-5">

              {/* Columna Izquierda: Propuesta de Valor cercana y versátil */}
              {/* TASK-161: en celular el formulario de ingreso va siempre primero y el texto después
                  (antes solo al tocar "Ingresar", TASK-149). En escritorio quedan lado a lado. */}
              <Col lg={7} className="text-center text-lg-start pe-lg-5 order-2 order-lg-1">
                <div className="animate-fade-up">
                  <span className="badge bg-primary bg-opacity-10 text-primary fw-bold px-3 py-2 rounded-pill mb-3 border border-primary border-opacity-25" style={{ fontSize: '0.85rem' }}>
                    AGENDA + TURNOS ONLINE + CRM ⚡
                  </span>
                  <h1 className="hero-title">
                    Agenda, turnos online y CRM de clientes.<br />
                    <span className="hero-title-highlight">Sin plantones. Sin complicaciones.</span>
                  </h1>
                </div>

                <p className="hero-subtitle mb-4">
                  Tus clientes reservan solos a cualquier hora, cobrás señas por <strong>Mercado Pago</strong> para que nadie te deje colgado y cada cliente queda con su ficha, su historial y sus etiquetas. Turnos, equipo y clientes en un solo lugar.
                </p>

                {/* Badges de Confianza con alto contraste */}
                <div className="d-flex flex-wrap justify-content-center justify-content-lg-start gap-2 gap-sm-3 mb-4 text-dark small fw-semibold">
                  <span className="d-flex align-items-center gap-1 text-dark"><FaCheckCircle className="text-success" /> {TRIAL_DAYS} días de prueba gratis</span>
                  <span className="d-flex align-items-center gap-1 text-dark"><FaCheckCircle className="text-success" /> Sin tarjeta de crédito</span>
                  <span className="d-flex align-items-center gap-1 text-dark"><FaCheckCircle className="text-success" /> Listo en 3 minutos</span>
                </div>

                {/* Botones de Acción con touch targets de 44px */}
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start">
                  <a href="#pricing" className="btn btn-uturno-primary btn-lg d-flex align-items-center justify-content-center gap-2" style={{ minHeight: '44px' }}>
                    Comenzar Demo Gratis <FaArrowRight />
                  </a>

                  <Link to="/reserva" className="btn btn-uturno-secondary btn-lg d-flex align-items-center justify-content-center" style={{ minHeight: '44px' }}>
                    Buscar Turno como Cliente
                  </Link>

                  {canInstall && (
                    <button onClick={handleInstallClick} className="btn btn-outline-secondary btn-lg d-flex align-items-center justify-content-center gap-2" style={{ minHeight: '44px' }}>
                      <FaDownload /> Descargar App
                    </button>
                  )}
                </div>
              </Col>

              {/* Columna Derecha: Tarjeta de Acceso / Registro (Padding 16px en móvil) */}
              <Col lg={5} className="order-1 order-lg-2 d-flex justify-content-center">
                <div id="login-card" className="uturno-card w-100 p-3 p-md-5" style={{ maxWidth: '440px' }}>
                  {isAuthenticated ? (
                    (() => {
                      const needsOnboarding = user && (user.onboarding_completed === false || user.onboarding_completed === 0 || user.onboarding_completed === 'false');
                      return (
                        <div className="text-center py-3">
                          <div className="d-inline-flex justify-content-center align-items-center bg-primary bg-opacity-10 text-primary rounded-circle mb-3" style={{ width: 70, height: 70 }}>
                            <FaBuilding size={32} />
                          </div>
                          <h3 className="fw-bold fs-4 mb-2 text-dark">
                            {needsOnboarding ? '¡Falta 1 paso!' : '¡Hola de nuevo!'}
                          </h3>
                          <p className="text-muted small mb-4">
                            {needsOnboarding
                              ? 'Completa la configuración inicial de tu negocio.' 
                              : 'Tu panel de administración está activo.'}
                          </p>
                          <Link 
                            to={needsOnboarding ? '/onboarding' : '/dashboard'} 
                            className="btn btn-uturno-primary w-100 mb-3 py-2 d-flex align-items-center justify-content-center gap-2"
                          >
                            {needsOnboarding ? 'Completar Configuración' : 'Acceder a mi Panel'} <FaArrowRight />
                          </Link>
                          <button onClick={logout} className="btn btn-link text-muted text-decoration-none small shadow-none">
                            Cerrar sesión
                          </button>
                        </div>
                      );
                    })()
                  ) : (
                    <Suspense fallback={<div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>}>
                      {isLoginView ? (
                        <LoginForm onSwitchToRegister={() => setIsLoginView(false)} />
                      ) : (
                        <RegisterForm onSwitchToLogin={() => setIsLoginView(true)} />
                      )}
                    </Suspense>
                  )}
                </div>
              </Col>

            </Row>
          </Container>
        </section>

        {/* =======================================
            SECCIÓN 2: RUBROS E INDUSTRIAS
        ======================================= */}
        <section id="industries" className="py-4 py-md-5 bg-white border-top border-bottom">
          <Container className="py-2 py-md-4 px-3">
            <div className="text-center mb-4 mb-md-5">
              <span className="badge bg-primary bg-opacity-10 text-primary fw-bold px-3 py-2 rounded-pill mb-2">
                ADAPTADO A TU NEGOCIO
              </span>
              <h2 className="display-6 fw-bold text-dark mb-3">Un Software Diseñado para tu Rubro</h2>
              <p className="lead text-muted mx-auto" style={{ maxWidth: '680px' }}>
                Uturno adapta los campos de la ficha del cliente y la agenda según las necesidades específicas de tu sector.
              </p>
            </div>

            {/* Selector de Pestañas de Rubros con Touch Targets >= 44px */}
            <div className="d-flex flex-wrap justify-content-center gap-2 mb-4">
              <button
                className={`btn ${activeTab === 'peluqueria' ? 'btn-primary' : 'btn-outline-secondary'} d-inline-flex align-items-center justify-content-center`}
                style={{ minHeight: '44px', padding: '8px 14px', borderRadius: '10px' }}
                onClick={() => setActiveTab('peluqueria')}
              >
                <FaCut className="me-2" /> Peluquerías & Barberías
              </button>
              <button
                className={`btn ${activeTab === 'veterinaria' ? 'btn-primary' : 'btn-outline-secondary'} d-inline-flex align-items-center justify-content-center`}
                style={{ minHeight: '44px', padding: '8px 14px', borderRadius: '10px' }}
                onClick={() => setActiveTab('veterinaria')}
              >
                <FaDog className="me-2" /> Veterinarias
              </button>
              <button
                className={`btn ${activeTab === 'taller' ? 'btn-primary' : 'btn-outline-secondary'} d-inline-flex align-items-center justify-content-center`}
                style={{ minHeight: '44px', padding: '8px 14px', borderRadius: '10px' }}
                onClick={() => setActiveTab('taller')}
              >
                <FaCar className="me-2" /> Talleres Mecánicos
              </button>
              <button
                className={`btn ${activeTab === 'salud' ? 'btn-primary' : 'btn-outline-secondary'} d-inline-flex align-items-center justify-content-center`}
                style={{ minHeight: '44px', padding: '8px 14px', borderRadius: '10px' }}
                onClick={() => setActiveTab('salud')}
              >
                <FaHeartbeat className="me-2" /> Salud & Estética
              </button>
              <button
                className={`btn ${activeTab === 'deporte' ? 'btn-primary' : 'btn-outline-secondary'} d-inline-flex align-items-center justify-content-center`}
                style={{ minHeight: '44px', padding: '8px 14px', borderRadius: '10px' }}
                onClick={() => setActiveTab('deporte')}
              >
                <FaFootballBall className="me-2" /> Deportes & Canchas
              </button>
            </div>

            {/* Detalle de Ficha por Rubro (Padding 16px en móvil) */}
            <div className="uturno-card p-3 p-md-5 max-w-3xl mx-auto">
              {activeTab === 'peluqueria' && (
                <Row className="align-items-center">
                  <Col md={7}>
                    <h3 className="fw-bold mb-3">✂️ Peluquerías, Barberías & Centros de Estética</h3>
                    <p className="text-muted">Permite a tus clientes elegir al profesional de su preferencia, seleccionar servicios combinados y abonar la seña previa.</p>
                    <ul className="list-unstyled text-muted small d-flex flex-column gap-2 mb-0">
                      <li><FaCheckCircle className="text-success me-2" /> <strong>Ficha de Cliente:</strong> Guardado de fórmula de tinte, tipo de cabello y alergias.</li>
                      <li><FaCheckCircle className="text-success me-2" /> <strong>Liquidación de Comisiones:</strong> % configurable por profesional.</li>
                      <li><FaCheckCircle className="text-success me-2" /> <strong>Agenda Multiprofesional:</strong> Evita superposiciones de sillones.</li>
                    </ul>
                  </Col>
                  <Col md={5} className="text-center mt-3 mt-md-0">
                    <div className="p-4 bg-light rounded-4 border">
                      <span className="badge bg-primary mb-2">Ficha de Cliente</span>
                      <div className="fw-bold text-dark fs-5">Sofía Ramírez</div>
                      <div className="text-muted small">Tinte: Tono 7.3 + Balayage</div>
                      <div className="text-muted small">Preferencias: Cafecito al llegar</div>
                    </div>
                  </Col>
                </Row>
              )}

              {activeTab === 'veterinaria' && (
                <Row className="align-items-center">
                  <Col md={7}>
                    <h3 className="fw-bold mb-3">🐾 Veterinarias & Peluquerías Caninas</h3>
                    <p className="text-muted">Registra a las mascotas de tus clientes y mantén el historial clínico y de baños al día en cada visita.</p>
                    <ul className="list-unstyled text-muted small d-flex flex-column gap-2 mb-0">
                      <li><FaCheckCircle className="text-success me-2" /> <strong>Ficha de Mascota:</strong> Nombre, Especie, Raza, Peso y Alergias.</li>
                      <li><FaCheckCircle className="text-success me-2" /> <strong>Recordatorios Automáticos:</strong> Vacunación y desparasitación.</li>
                    </ul>
                  </Col>
                  <Col md={5} className="text-center mt-3 mt-md-0">
                    <div className="p-4 bg-light rounded-4 border">
                      <span className="badge bg-success mb-2">Ficha de Mascota</span>
                      <div className="fw-bold text-dark fs-5">Manchas (Gato)</div>
                      <div className="text-muted small">Raza: Siamés • Peso: 4.2 kg</div>
                      <div className="text-muted small text-danger">Alergia: Penicilina</div>
                    </div>
                  </Col>
                </Row>
              )}

              {activeTab === 'taller' && (
                <Row className="align-items-center">
                  <Col md={7}>
                    <h3 className="fw-bold mb-3">🚗 Talleres Mecánicos & Lubricentros</h3>
                    <p className="text-muted">Organiza las elevadoras y turnos de service registrando la patente y datos del vehículo del cliente.</p>
                    <ul className="list-unstyled text-muted small d-flex flex-column gap-2 mb-0">
                      <li><FaCheckCircle className="text-success me-2" /> <strong>Ficha de Vehículo:</strong> Patente, Marca, Modelo y Año.</li>
                      <li><FaCheckCircle className="text-success me-2" /> <strong>Historial de Services:</strong> Cambio de aceite, frenos y distribución.</li>
                    </ul>
                  </Col>
                  <Col md={5} className="text-center mt-3 mt-md-0">
                    <div className="p-4 bg-light rounded-4 border">
                      <span className="badge bg-warning text-dark mb-2">Ficha de Vehículo</span>
                      <div className="fw-bold text-dark fs-5">Patente: AA 123 CD</div>
                      <div className="text-muted small">Toyota Hilux (2022)</div>
                      <div className="text-muted small">Último Service: 15/05/2026</div>
                    </div>
                  </Col>
                </Row>
              )}

              {activeTab === 'salud' && (
                <Row className="align-items-center">
                  <Col md={7}>
                    <h3 className="fw-bold mb-3">⚕️ Consultorios, Kinesiología & Odontología</h3>
                    <p className="text-muted">Gestión confidencial de pacientes con reserva de sobreturnos y señas previas para garantizar asistencia.</p>
                    <ul className="list-unstyled text-muted small d-flex flex-column gap-2 mb-0">
                      <li><FaCheckCircle className="text-success me-2" /> <strong>Ficha Médica:</strong> Antecedentes, medicación y notas del tratamiento.</li>
                      <li><FaCheckCircle className="text-success me-2" /> <strong>Mercado Pago:</strong> Cobro anticipado de consulta.</li>
                    </ul>
                  </Col>
                  <Col md={5} className="text-center mt-3 mt-md-0">
                    <div className="p-4 bg-light rounded-4 border">
                      <span className="badge bg-info text-dark mb-2">Paciente Salud</span>
                      <div className="fw-bold text-dark fs-5">Carlos Gómez</div>
                      <div className="text-muted small">Kinesiología - Rodilla Izq.</div>
                      <div className="text-muted small">Sesión 4 de 10</div>
                    </div>
                  </Col>
                </Row>
              )}

              {activeTab === 'deporte' && (
                <Row className="align-items-center">
                  <Col md={7}>
                    <h3 className="fw-bold mb-3">⚽ Clubes, Canchas & Gimnasios</h3>
                    <p className="text-muted">Reserva de canchas de fútbol, pádel o turnos de personal trainer con señas automáticas en minutos.</p>
                    <ul className="list-unstyled text-muted small d-flex flex-column gap-2 mb-0">
                      <li><FaCheckCircle className="text-success me-2" /> <strong>Ficha Deportiva:</strong> Disciplina, lesiones y objetivos.</li>
                      <li><FaCheckCircle className="text-success me-2" /> <strong>Reserva por Espacios:</strong> Asignación de Canchas 1 a 6.</li>
                    </ul>
                  </Col>
                  <Col md={5} className="text-center mt-3 mt-md-0">
                    <div className="p-4 bg-light rounded-4 border">
                      <span className="badge bg-dark mb-2">Reserva de Cancha</span>
                      <div className="fw-bold text-dark fs-5">Cancha 3 - Pádel</div>
                      <div className="text-muted small">Horario: 20:00 a 21:30 hs</div>
                      <div className="text-success fw-bold small">Seña Pagada: $8.000 ARS</div>
                    </div>
                  </Col>
                </Row>
              )}
            </div>
          </Container>
        </section>

        {/* =======================================
            SECCIÓN 3: MÓDULOS Y CARACTERÍSTICAS
        ======================================= */}
        <section id="features" className="py-4 py-md-5">
          <Container className="py-2 py-md-4 px-3">
            <div className="text-center mb-4 mb-md-5">
              <h2 className="display-6 fw-bold text-dark mb-3">Todo lo que incluye uTurno</h2>
              <p className="lead text-muted mx-auto" style={{ maxWidth: '650px' }}>
                Herramientas diseñadas para ahorrarte horas de atención por WhatsApp y maximizar la rentabilidad de tu negocio.
              </p>
            </div>

            {/* Grilla con separación de 16px en móvil (g-3) y 24px en desktop (g-md-4) */}
            <Row className="g-3 g-md-4">
              <Col md={6} lg={3}>
                <div className="uturno-card p-3 p-md-4 h-100">
                  <div className="bg-primary bg-opacity-10 text-primary p-2 p-md-3 rounded-3 d-inline-block mb-3">
                    <FaWallet size={24} />
                  </div>
                  <h3 className="h5 fw-bold mb-2">Señas por Mercado Pago</h3>
                  <p className="text-muted small mb-0">
                    Exige una seña obligatoria al reservar. El dinero va directo a tu cuenta de Mercado Pago en Pesos Argentinos.
                  </p>
                </div>
              </Col>

              <Col md={6} lg={3}>
                <div className="uturno-card p-3 p-md-4 h-100">
                  <div className="bg-success bg-opacity-10 text-success p-2 p-md-3 rounded-3 d-inline-block mb-3">
                    <FaUserTie size={24} />
                  </div>
                  <h3 className="h5 fw-bold mb-2">Comisiones de Equipo</h3>
                  <p className="text-muted small mb-0">
                    Configura el % de comisión de cada empleado y calcula automáticamente el sueldo a liquidar a fin de mes.
                  </p>
                </div>
              </Col>

              <Col md={6} lg={3}>
                <div className="uturno-card p-3 p-md-4 h-100">
                  <div className="bg-warning bg-opacity-10 text-warning p-2 p-md-3 rounded-3 d-inline-block mb-3">
                    <FaAddressBook size={24} />
                  </div>
                  <h3 className="h5 fw-bold mb-2">CRM Adaptativo</h3>
                  <p className="text-muted small mb-0">
                    Historial de visitas y campos personalizados según tu rubro (Mascotas, Patentes, Tintes, Antecedentes).
                  </p>
                </div>
              </Col>

              <Col md={6} lg={3}>
                <div className="uturno-card p-3 p-md-4 h-100">
                  <div className="bg-info bg-opacity-10 text-info p-2 p-md-3 rounded-3 d-inline-block mb-3">
                    <FaPalette size={24} />
                  </div>
                  <h3 className="h5 fw-bold mb-2">Branding Institucional</h3>
                  <p className="text-muted small mb-0">
                    Sube tu logo y elige los colores institucionales para que tu página pública refleje la identidad de tu marca.
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* =======================================
            SECCIÓN 4: PRECIOS TRANSPARENTES
        ======================================= */}
        <section id="pricing" className="py-4 py-md-5 bg-white border-top">
          <Container fluid className="py-2 py-md-4 px-3 px-xl-5" style={{ maxWidth: '1400px' }}>
            <div className="text-center mb-4">
              <span className="badge bg-success bg-opacity-10 text-success fw-bold px-3 py-2 rounded-pill mb-2">
                PLAN TRANSPARENTE
              </span>
              <h2 className="display-6 fw-bold text-dark mb-3">Una Inversión que se Paga Sola</h2>
              <p className="lead text-muted mx-auto mb-4" style={{ maxWidth: '650px' }}>
                Plan Básico gratis para siempre o 15 días de prueba en planes Pro sin tarjeta de crédito.
              </p>

              {/* Toggle de facturación mensual / anual con touch target de 44px */}
              <div className="d-flex align-items-center justify-content-center gap-3 mb-4" style={{ minHeight: '44px' }}>
                <span
                  onClick={() => setBillingCycle('monthly')}
                  className={`fw-bold small cursor-pointer ${billingCycle === 'monthly' ? 'text-dark' : 'text-muted'}`}
                  style={{ cursor: 'pointer', padding: '8px 4px' }}
                >
                  Facturación Mensual
                </span>
                <div
                  onClick={() => setBillingCycle((b) => (b === 'monthly' ? 'yearly' : 'monthly'))}
                  style={{
                    width: '48px',
                    height: '26px',
                    backgroundColor: billingCycle === 'yearly' ? '#2563eb' : '#cbd5e1',
                    borderRadius: '13px',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'background-color 0.2s',
                  }}
                >
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#ffffff',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '3px',
                      left: billingCycle === 'yearly' ? '25px' : '3px',
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }}
                  />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span
                    onClick={() => setBillingCycle('yearly')}
                    className={`fw-bold small cursor-pointer ${billingCycle === 'yearly' ? 'text-dark' : 'text-muted'}`}
                    style={{ cursor: 'pointer', padding: '8px 4px' }}
                  >
                    Facturación Anual
                  </span>
                  <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 rounded-pill px-2 py-1" style={{ fontSize: '0.7rem' }}>
                    2 meses gratis
                  </span>
                </div>
              </div>
            </div>

            <Row className="justify-content-center g-3 g-md-4">
              {PLANES.map((plan) => {
                const precio = billingCycle === 'monthly' ? plan.precioMensual : plan.precioAnual;

                return (
                  <Col lg={4} xl md={6} key={plan.id}>
                    <div
                      className={`uturno-card h-100 p-3 p-md-4 d-flex flex-column ${
                        plan.destacado ? 'border-2 border-primary position-relative shadow' : 'border'
                      }`}
                      style={{ borderRadius: '14px', backgroundColor: '#ffffff' }}
                    >
                      {plan.destacado && (
                        <span className="badge bg-primary position-absolute top-0 end-0 m-3 px-3 py-2 rounded-pill">
                          MÁS POPULAR
                        </span>
                      )}
                      <h3 className="fw-bold text-dark mb-1 fs-4">
                        {plan.name}
                      </h3>
                      <p className="text-muted small mb-3" style={{ minHeight: '38px' }}>{plan.descripcion}</p>

                      {/* Bloque de Precios */}
                      <div className="mb-3">
                        <div className="d-flex align-items-baseline gap-1">
                          <span className="fs-2 fw-bold text-dark">
                            {plan.precioMensual === 0
                              ? '$0'
                              : plan.isEnterprise && billingCycle === 'yearly'
                              ? 'A convenir'
                              : plan.isEnterprise
                              ? `Desde ${formatPrecioARS(precio)}`
                              : formatPrecioARS(precio)}
                          </span>
                          <span className="text-muted small">
                            {plan.precioMensual === 0
                              ? ' Gratis'
                              : plan.isEnterprise && billingCycle === 'yearly'
                              ? ''
                              : ` ARS / ${billingCycle === 'monthly' ? 'mes' : 'año'}`}
                          </span>
                        </div>
                        {billingCycle === 'yearly' && plan.precioMensual > 0 && plan.precioAnual && (
                          <div className="text-success small fw-bold mt-1">
                            Equivale a {formatPrecioARS(Math.round(plan.precioAnual / 12))}/mes
                          </div>
                        )}
                      </div>

                      {/* Medidor de turnos mensuales */}
                      <div className="d-flex align-items-center gap-2 mb-3 px-3 py-2 rounded-3" style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                        <span>📊</span>
                        <span className="small fw-bold" style={{ color: '#0369a1' }}>
                          {plan.turnosMensuales ? `${plan.turnosMensuales.toLocaleString('es-AR')} turnos/mes` : 'Turnos ilimitados'}
                        </span>
                      </div>

                      {/* Lista de Features */}
                      <ul className="list-unstyled d-flex flex-column gap-2 mb-4 text-secondary flex-grow-1 small">
                        {plan.features.map((feature) => (
                          <li key={feature} className="d-flex align-items-center gap-2">
                            <FaCheckCircle className="text-success flex-shrink-0" size={13} /> {feature}
                          </li>
                        ))}
                      </ul>

                      {/* Botón CTA con touch target de 44px */}
                      {plan.isEnterprise ? (
                        <a
                          href="mailto:soporte@uturno.com?subject=Consulta%20Plan%20Enterprise%20uTurno"
                          className="btn btn-outline-primary btn-lg w-100 py-2 fw-bold text-decoration-none text-center d-flex align-items-center justify-content-center mt-auto rounded-3"
                          style={{ fontSize: '0.95rem', minHeight: '44px' }}
                        >
                          Contactar Ventas
                        </a>
                      ) : (
                        <button
                          onClick={() => {
                            setIsLoginView(false);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className={`btn ${plan.destacado ? 'btn-uturno-primary' : 'btn-outline-primary'} btn-lg w-100 py-2 fw-bold mt-auto rounded-3 d-flex align-items-center justify-content-center`}
                          style={{ fontSize: '0.95rem', minHeight: '44px' }}
                        >
                          {plan.precioMensual === 0
                            ? 'Comenzar Gratis'
                            : `Probar Gratis 15 Días`}
                        </button>
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>

            {/* Banner Contenedor del Plan Builder Personalizado con espaciado responsive (mt-4 en móvil: 24px) */}
            <div className="mt-4 mt-md-5 p-3 p-md-4 bg-light rounded-4 border">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                <div className="text-center text-md-start">
                  <span className="badge bg-primary bg-opacity-10 text-primary fw-bold px-3 py-1 rounded-pill mb-2">
                    ⚡ 100% PERSONALIZABLE
                  </span>
                  <h3 className="h4 fw-bold text-dark mb-1">¿Necesitás más turnos, sedes o empleados?</h3>
                  <p className="text-muted small mb-0">
                    Armá tu plan combinando exactamente las características y cupos que necesita tu negocio.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsPlanBuilderOpen(!isPlanBuilderOpen)}
                  className="btn px-4 py-2 fw-bold rounded-pill shadow-sm d-inline-flex align-items-center justify-content-center gap-2 flex-shrink-0 transition-all"
                  style={{
                    backgroundColor: isPlanBuilderOpen ? '#eff6ff' : '#2563eb',
                    color: isPlanBuilderOpen ? '#1d4ed8' : '#ffffff',
                    border: isPlanBuilderOpen ? '1px solid #bfdbfe' : '1px solid #2563eb',
                    fontSize: '0.9rem',
                    minHeight: '44px'
                  }}
                >
                  <FaBolt /> {isPlanBuilderOpen ? 'Ocultar Configurador' : 'Armá tu Plan a Medida'}
                </button>
              </div>

              {/* Plan Builder Desplegable Inline */}
              {isPlanBuilderOpen && (
                <div className="mt-4 pt-4 border-top">
                  <Suspense fallback={<div className="text-center py-4"><Spinner animation="border" size="sm" /></div>}>
                    <PlanBuilder
                      initialBasePlan="profesional"
                      initialBillingCycle={billingCycle}
                      onCheckout={(planData) => {
                        setIsLoginView(false);
                        const registerSection = document.getElementById('login-section');
                        if (registerSection) registerSection.scrollIntoView({ behavior: 'smooth' });
                      }}
                    />
                  </Suspense>
                </div>
              )}
            </div>
          </Container>
        </section>

        {/* =======================================
            SECCIÓN 5: PREGUNTAS FRECUENTES (FAQ)
        ======================================= */}
        <section id="faq" className="py-4 py-md-5 bg-light border-top">
          <Container className="py-2 py-md-4 px-3" style={{ maxWidth: '800px' }}>
            <div className="text-center mb-4 mb-md-5">
              <h2 className="display-6 fw-bold text-dark mb-3">Preguntas Frecuentes</h2>
              <p className="text-muted">Resolvemos tus dudas antes de comenzar tu prueba gratis.</p>
            </div>

            <Accordion defaultActiveKey="0" className="uturno-accordion">
              <Accordion.Item eventKey="0" className="mb-3 border rounded-3 overflow-hidden">
                <Accordion.Header className="fw-bold">
                  <FaQuestionCircle className="text-primary me-2" /> ¿Cómo funcionan las señas por Mercado Pago?
                </Accordion.Header>
                <Accordion.Body className="text-muted">
                  Conectas tu cuenta de Mercado Pago desde la sección de Configuración en 1 solo clic. Al reservar un turno, el sistema le solicita la seña configurada por ti y la acredita instantáneamente en tu cuenta en Pesos Argentinos.
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="1" className="mb-3 border rounded-3 overflow-hidden">
                <Accordion.Header className="fw-bold">
                  <FaQuestionCircle className="text-primary me-2" /> ¿Mis clientes tienen que descargar una aplicación?
                </Accordion.Header>
                <Accordion.Body className="text-muted">
                  No. Tus clientes acceden directamente a tu enlace personalizado (ej: <code>uturno.com/@tu_negocio</code>) desde cualquier navegador web en su celular o computadora sin necesidad de instalar nada.
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="2" className="mb-3 border rounded-3 overflow-hidden">
                <Accordion.Header className="fw-bold">
                  <FaQuestionCircle className="text-primary me-2" /> ¿Cómo se calculan las comisiones de los empleados?
                </Accordion.Header>
                <Accordion.Body className="text-muted">
                  En el módulo de Equipo asignas un % de comisión a cada profesional (ej: 50%). El sistema multiplica automáticamente las señas y precios de los turnos completados por ese profesional y te genera el reporte listo para liquidar sueldos a fin de mes.
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="3" className="mb-3 border rounded-3 overflow-hidden">
                <Accordion.Header className="fw-bold">
                  <FaQuestionCircle className="text-primary me-2" /> ¿Qué pasa cuando terminan los 15 días de prueba?
                </Accordion.Header>
                <Accordion.Body className="text-muted">
                  Durante los 15 días puedes probar todas las funcionalidades gratis. Al finalizar, si decides continuar, activas tu suscripción desde {formatPrecioARS(Math.min(...PLANES.filter((p) => p.precioMensual > 0).map((p) => p.precioMensual)))} ARS/mes o puedes mantenerte en el Plan Básico Gratuito. No realizamos cobros automáticos sin tu consentimiento.
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Container>
        </section>

      </main>
    </PublicLayout>
  );
};

export default HomePage;

